import { SBaseDisplay, SDate, SEnumTypeMapping, assignSafe, isString } from '@summit/core';
import {
  CellClassFunc,
  ColDef,
  ColGroupDef,
  HeaderClass,
  ISetFilterParams,
  SideBarDef,
  ValueFormatterParams
} from 'ag-grid-enterprise';

import { AppConfig } from '../../services';
import { DisplayType, DisplayTypeSingleLine } from '../display-type.enum';
import { EquationTypes } from '../equation-types.enum';
import { FilterParameter } from '../filter-parameter';
import { IDisplaySchema } from '../i-display-schema';
import { IProfile } from '../i-profile';
import { SortAttribute } from '../sort-attribute';
import { SummitAggregateFunctions } from '../summit-aggregate-functions';
import { SummitDisplay } from '../summit-display';
import { SummitGridCallbacks } from '../summit-grid-callbacks';
import { SummitGridCellFunctions } from '../summit-grid-cell-functions';
import { SummitIdObject } from '../summit-id-object';
import { DateBarTypes, ViewConfig } from './view-config';
import { ViewField } from './view-field';
import { ViewType } from './view-info';

export class ViewModel<T extends SummitIdObject> extends SummitIdObject {
  //
  // Properties
  //
  private _columnFilterDefault: (keyof T & string)[] | undefined;
  private _columnFilterActive: (keyof T & string)[] | undefined;
  override id!: string;
  name?: string;
  type?: ViewType;
  serverObjectType: string | undefined;
  dateBar?: DateBarTypes;
  dateBarLabel?: string;

  completeColumnList?: ViewField<T>[];
  filterStringDefault?: string;
  private _filterStringActive?: string;
  filterParametersDefault?: FilterParameter[];
  private _filterParametersActive?: FilterParameter[] | undefined;
  sortParametersDefault?: SortAttribute<T>[];
  private _sortParametersActive?: SortAttribute<T>[] | undefined;
  private _columnViewActiveInOrder: (keyof T & string)[] | undefined;
  public groupByDefault?: (keyof T & string)[];
  public groupByShiftToColumns?: number;
  public groupShiftToColumnsAfter?: number;
  public pinnedColumnsDefault?: (keyof T & string)[];
  private _pinnedColumnsActive: (keyof T & string)[] | undefined;
  public viewBy?: (keyof T & string)[];
  //headers
  public showNewButton?: boolean; //used for grid
  public lockColumns?: boolean;
  public suppressColumnMenus?: boolean;
  //Pivot
  public pivotColumnDefault: (keyof T & string)[] | undefined;
  private _pivotColumnActive: (keyof T & string)[] | undefined;

  //Footers
  public showFooter?: boolean;
  public showGroupFooters?: boolean;
  public showGroupFooterOfOne?: (singleEntry: T | undefined) => boolean;
  //Footers: Leave undefined to show for all groups
  public groupFooterRows: (keyof T & string)[] | undefined;
  //AG-Grid New
  dragEnabled?: (keyof T & string)[];
  selectable?: 'single' | 'multiple' | undefined;
  rowMultiSelectWithClick?: boolean;
  groupColName?: string | null;
  sideBar?: string[] | SideBarDef;
  scheduleSummary?: string;
  private _groupBy?: (keyof T & string)[];

  //can be givin a partial view model to create a new one
  constructor(jsonData: Partial<ViewConfig<T>> | Partial<ViewModel<T>> | undefined, schema: IDisplaySchema<T>) {
    super();
    //console.log(`${schema.refServerObjectName}: Create new new ViewModel based on `, jsonData, schema);
    if (jsonData) {
      if (jsonData.id && typeof jsonData.id === 'string') this.id = jsonData.id;
      if (jsonData.dateBar) this.dateBar = jsonData.dateBar;
      if (jsonData.dateBarLabel) this.dateBarLabel = jsonData.dateBarLabel;
      if (jsonData.name) this.name = jsonData.name;
      if (jsonData.type || jsonData.type === 0) this.type = jsonData.type;
      if (jsonData.lockColumns) this.lockColumns = jsonData.lockColumns;
      if (jsonData.suppressColumnMenus) this.suppressColumnMenus = jsonData.suppressColumnMenus;
      if ((jsonData as any).columnOrder) {
        this.completeColumnList = schema.getColumnAttributeList((jsonData as any).columnOrder);
      } else if ((jsonData as any)['completeColumnList']) {
        this.completeColumnList = (jsonData as any)['completeColumnList'];
      } else {
        this.completeColumnList = schema.getColumnAttributeList();
      }
      //console.log("Column List", this.completeColumnList);
      if ((jsonData as any).viewColumns) {
        this._columnViewDefault = (jsonData as any).viewColumns;
      } else if ((jsonData as any)['_columnViewDefault']) {
        this._columnViewDefault = (jsonData as any)['_columnViewDefault'];
      }
      if ((jsonData as Partial<ViewConfig<T>>).pivotColumns) {
        this.pivotColumnDefault = (jsonData as Partial<ViewConfig<T>>).pivotColumns;
      }
      if ((jsonData as any).filterColumns) {
        // console.log(`loading filter defaults for ${this.name}`, jsonData.filterColumns);
        this.columnFilterDefault = (jsonData as any).filterColumns as (keyof T & string)[];
      } else if ((jsonData as any)['_columnFilterDefault']) {
        // console.log(`loading filter defaults for ${this.name}`, jsonData._columnFilterDefault);
        this.columnFilterDefault = (jsonData as any)['_columnFilterDefault'];
      }
      // Load super set of filter parameters
      if (schema.refFilterParameters) {
        //console.log(`[ViewModel.Constructor] Copying Filter ParametersDefault`)
        this.filterParametersDefault = schema.refFilterParameters.map((data) => {
          return new FilterParameter(data);
        });
        //console.log(`[ViewModel.Constructor] Copied Filter ParametersDefault \n${JSON.stringify(this.filterParametersDefault, null, 2)}`)
      } else {
        this.filterParametersDefault = new Array<FilterParameter>();
      }
      // Load View config's filter parameters
      if ((jsonData as any).filterParameters) {
        this.filterParametersDefault.forEach((element) => {
          if (Array.isArray((jsonData as any).filterParameters)) {
            const updateOverlay = (jsonData as any).filterParameters.find((entry: any) => entry.id == element.id);
            if (updateOverlay) {
              //console.log(`Updating \n ${JSON.stringify(element, null, 2)} with \n ${JSON.stringify(updateOverlay, null, 2)}`)
              assignSafe(element, updateOverlay);
              //console.log(`Updated Value: \n${JSON.stringify(element, null, 2)}`)
            }
          }
          /*
                    else if (jsonData['filterParameters'].hasOwnProperty(element.id)) {
                        element.state = jsonData.filterParameters[element.id] === 'true' ? true : false;
                        // console.log(`View ${viewConfig.name} - Loaded ${viewConfig.filterParameters[element.id]} into ${element.id}`);
                    }*/
        });
      } else if ((jsonData as any)['filterParametersDefault']) {
        this.filterParametersDefault = (jsonData as any)['filterParametersDefault'];
      }
      if ((jsonData as any).sortParameters || (jsonData as any)['sortParametersDefault']) {
        const sortValue = (jsonData as any).sortParameters ?? (jsonData as any)['sortParametersDefault'];
        if (Array.isArray(sortValue) || sortValue?.[0]) {
          this.sortParametersDefault ??= [];
          Object.keys(sortValue).forEach((key) => this.sortParametersDefault?.push(new SortAttribute(sortValue[key])));
        } else {
          this.sortParametersDefault = [new SortAttribute(sortValue)];
        }
      }
      this.groupByDefault = jsonData.groupBy ? jsonData.groupBy : [];
      this.pinnedColumnsDefault = (jsonData as any).pinnedColumns ?? (jsonData as any)['pinnedColumnsDefault'] ?? [];
      this.pivotColumnDefault = (jsonData as any).pivotColumns ?? (jsonData as any)['pivotColumnDefault'] ?? [];
      if (jsonData.groupByShiftToColumns) this.groupByShiftToColumns = jsonData.groupByShiftToColumns;
      if (jsonData.groupShiftToColumnsAfter) this.groupShiftToColumnsAfter = jsonData.groupShiftToColumnsAfter;
      if (jsonData.viewBy) this.viewBy = jsonData.viewBy;
      this.showNewButton = jsonData.showNewButton ?? true;
      if (jsonData.showFooter) this.showFooter = jsonData.showFooter;
      if (jsonData.showGroupFooterOfOne) this.showGroupFooterOfOne = jsonData.showGroupFooterOfOne;
      //console.log('selectable', jsonData.selectable);
      if (jsonData.selectable) this.selectable = jsonData.selectable;
      if (jsonData.rowMultiSelectWithClick) this.rowMultiSelectWithClick = jsonData.rowMultiSelectWithClick;
      if (jsonData.dragEnabled) this.dragEnabled = jsonData.dragEnabled.map((item) => item);
      if (jsonData.groupFooterRows) this.groupFooterRows = [...jsonData.groupFooterRows];
      this.showGroupFooters = jsonData.showGroupFooters ?? false;
      this.sideBar = Array.isArray(jsonData.sideBar) ? [...jsonData.sideBar] : (jsonData.sideBar ?? []);
    }
    if ((!this.columnFilterDefault || this.columnFilterDefault.length === 0) && this._columnViewDefault) {
      //console.log(this.columnFilterDefault, this._columnViewDefault)
      assignSafe(this.columnFilterDefault, this._columnViewDefault); //JEFF not sure how to handle null object assign in this case
      this.columnFilterDefault = this.columnFilterDefault.concat(this.groupBy);
    }
    this.serverObjectType = schema?.refServerObjectName;
    this.rowMultiSelectWithClick ??= false;
    this.showNewButton ??= true;
    this.lockColumns ??= false;
    this.suppressColumnMenus ??= true;
    //console.log(`New ViewModel ${this.id} .. based on ..`, this, jsonData);
  }

  //
  // Default Columns View
  //
  private _columnViewDefault?: (keyof T)[];
  get columnViewDefault(): (keyof T)[] {
    // console.log('_columnViewDefault = ' + this._columnViewDefault);
    if (!this._columnViewDefault) {
      if (!this.completeColumnList) {
        console.error(`View Model: ${this.name} has no column list defined.`);
        return [];
      }
      // console.log(`Computing _columnViewDefault`);
      this._columnViewDefault = this.completeColumnList
        .filter((entry) => entry.displayName && entry.key)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map((entry) => entry.key!);
    }
    return this._columnViewDefault;
  }
  set columnViewDefault(input: (keyof T)[]) {
    this._columnViewDefault = input;
  }
  //
  // Active Column View (unordered)
  //
  private _columnViewActive?: (keyof T & string)[];
  get columnViewActive(): (keyof T & string)[] {
    // console.log('_columnViewActive = ' + this._columnViewActive);
    if (!this._columnViewActive) {
      // console.log(`Computing _columnViewActive`);
      // console.log('_columnViewActive returning = ' + this._columnViewActive);
      this._columnViewActive = Object.assign([], this.columnViewDefault);
    }
    // default to default view
    // Note: Don't need to store separate until it is something different
    // console.log('_columnViewActive defaulting');
    // console.log('_columnViewActive returning = ' + this.columnViewDefault);
    return this._columnViewActive;
  }
  set columnViewActive(input: (keyof T & string)[]) {
    //console.log(`Clearing ViewActiveInOrder for new value`, input);
    //Remove Group By items from active view
    const temp = input.filter(
      (entry) => this.groupBy.indexOf(entry) == -1 && (!this.viewBy || this.viewBy.indexOf(entry) == -1)
    );
    this._columnViewActiveInOrder = undefined;
    //console.log(`Set ViewActiveInOrder to`, temp);
    this._columnViewActive = temp;
  }
  //
  // Active Column Order
  //
  private _columnOrderActive?: (keyof T & string)[];
  get columnOrderActive(): (keyof T & string)[] {
    // Assign Default if empty
    if (!this._columnOrderActive) {
      if (!this.completeColumnList || this.completeColumnList.length === 0) {
        console.error(`View Model: ${this.name} has no column list defined.`);
        return [];
      }
      this._columnOrderActive = this.completeColumnList.map((entry) => entry.key as keyof T & string);
    }
    return this._columnOrderActive;
  }
  set columnOrderActive(input: (keyof T & string)[]) {
    // console.log(`Clearing ViewActiveInOrder`);
    this._columnViewActiveInOrder = undefined;
    this._columnOrderActive = input;
  }
  //
  // Unified Active Columns in correct order
  //
  getColumnViewActiveInOrder(): (keyof T & string)[] {
    if (!this._columnViewActiveInOrder) {
      if (this.columnOrderActive && this.columnViewActive) {
        this._columnViewActiveInOrder = this.columnOrderActive.filter(
          (entry) => this.columnViewActive.indexOf(entry) >= 0
        );
      }
    }
    const response = Object.assign(new Array<keyof T & string>(), this._columnViewActiveInOrder);
    // console.log(`column view active in order: ${response.join(',')}`)
    return response;
  }

  getColumnViewActiveInOrderAttributes(): ViewField<T>[] {
    const response = new Array<ViewField<T>>();
    this.getColumnViewActiveInOrder().forEach((element) => {
      const item = this.completeColumnList?.find((entry) => entry.key === element);
      if (item) response.push(item);
    });
    return response;
  }

  getCompleteColumnOrderActive(): ViewField<T>[] {
    //console.log(`ViewModel: getCompleteColumnOrderActive`, this.columnOrderActive);
    const response = new Array<ViewField<T>>();
    this.columnOrderActive.forEach((element) => {
      const item = this.completeColumnList?.find((entry) => entry.key === element);
      if (item) response.push(item);
    });
    // console.timeEnd(`viewModel.getCompleteColumnOrderActive`);
    return response;
  }

  getCompleteAgColumnOrderActive(): ViewField<T>[] {
    console.log(`ViewModel: getCompleteColumnOrderActive`, this.columnOrderActive);
    const response = new Array<ViewField<T>>();
    this.columnOrderActive.forEach((element) => {
      const item = this.completeColumnList?.find((entry) => entry.key === element);
      if (item) response.push(item);
    });
    //console.log(`viewModel.getCompleteColumnOrderActive`, response);
    return response;
  }

  //
  // Active Column Order
  //

  get groupBy(): (keyof T & string)[] {
    // Assign Default if empty
    if (!this._groupBy) {
      if (!this.groupByDefault) {
        console.error(`View Model: ${this.name} has no group by default list defined.`);
        return [];
      }
      this._groupBy = [...this.groupByDefault];
    }
    return this._groupBy;
  }
  set groupBy(input: (keyof T & string)[]) {
    // console.log(`Clearing ViewActiveInOrder`);
    this._groupBy = input;
  }
  get pinnedColumnsActive(): (keyof T & string)[] {
    // Assign Default if empty
    if (!this._pinnedColumnsActive) {
      if (!this.pinnedColumnsDefault) {
        return [];
      }
      this._pinnedColumnsActive = [...this.pinnedColumnsDefault];
    }
    //console.log(`Returning Pinned Columns Active ${this._pinnedColumnsActive.join(',')}`);
    return this._pinnedColumnsActive;
  }
  set pinnedColumnsActive(input: (keyof T & string)[]) {
    // console.log(`Clearing ViewActiveInOrder`);
    this._pinnedColumnsActive = input;
  }

  /**
   *
   *
   * Filter Section
   *
   */
  isFilterDisabled(): boolean {
    // console.log('current column filter = ', this.columnFilterActive, this.columnFilterDefault, this);
    return this.columnFilterActive == null || this.columnFilterActive.length === 0;
  }
  //
  // Default Filter Columns
  //
  get columnFilterDefault(): (keyof T & string)[] {
    // console.log('_columnFilterDefault = ' + this._columnFilterDefault);
    if (!this._columnFilterDefault) {
      // console.log(`Computing columnFilterDefault`, this);
      this._columnFilterDefault = this.completeColumnList?.map((entry) => entry.key as keyof T & string) ?? [];
      //console.log(`Computed _columnFilterDefault ${this._columnFilterDefault.join(',')}`);
    }
    return this._columnFilterDefault ?? [];
  }
  set columnFilterDefault(input: (keyof T & string)[]) {
    //console.log(`Set columnFilterDefault ${input.join(',')} for ${this.id}`);
    this._columnFilterDefault = input;
  }
  //
  // Active Filter Columns
  //
  get columnFilterActive(): (keyof T & string)[] {
    if (!this._columnFilterActive) {
      // console.log(`Computing _columnFilterActive`);
      this._columnFilterActive = Object.assign([], this.columnFilterDefault);
      //console.log(`Computed _columnFilterActive ${this._columnFilterActive.join(',')}`);
    }
    return this._columnFilterActive;
  }
  set columnFilterActive(input: (keyof T & string)[]) {
    // console.log(`Set columnFilterActive ${input.join(',')} for ${this.id}`);
    this._columnFilterActive = input;
  }
  //
  // Filter String
  //
  get filterStringActive(): string {
    // console.log('_columnFilterActive = ' + this._columnFilterActive);
    if (!this._filterStringActive) {
      this._filterStringActive = this.filterStringDefault;
    }
    return this._filterStringActive ?? '';
  }
  set filterStringActive(input: string | undefined) {
    this._filterStringActive = input;
  }

  //
  // Active Filter Columns
  //
  get pivotColumnActive(): (keyof T & string)[] {
    if (!this._pivotColumnActive) {
      // console.log(`Computing _columnFilterActive`);
      this._pivotColumnActive = Object.assign([], this.pivotColumnDefault);
      //console.log(`Computed _columnFilterActive ${this._columnFilterActive.join(',')}`);
    }
    return this._pivotColumnActive;
  }
  set pivotColumnActive(input: (keyof T & string)[]) {
    // console.log(`Set columnFilterActive ${input.join(',')} for ${this.id}`);
    this._pivotColumnActive = input;
  }

  /**
   *
   * Sort Functions
   *
   */
  get sortParametersActive(): SortAttribute<T>[] {
    if (this._sortParametersActive === undefined) {
      if (this.sortParametersDefault) {
        this._sortParametersActive = this.sortParametersDefault.map((entry) => new SortAttribute(entry));
      } else this._sortParametersActive = [];
    }
    return this._sortParametersActive;
  }
  set sortParametersActive(input: SortAttribute<T> | SortAttribute<T>[]) {
    if (Array.isArray(input)) this._sortParametersActive = input;
    else this._sortParametersActive = [input];
  }

  //
  // Active Filter Parameters
  //
  get filterParametersActive(): FilterParameter[] {
    if (!this._filterParametersActive && this.filterParametersDefault) {
      // console.log('Initializing Filter Parameters Active from', this.filterParametersDefault);
      this._filterParametersActive = this.filterParametersDefault.map((entry) => new FilterParameter(entry));
    }
    // console.log(`Returning Active Filter Parameters ${JSON.stringify(this._filterParametersActive)}`);
    return Object.assign(new Array<FilterParameter>(), this._filterParametersActive);
  }
  set filterParametersActive(input: FilterParameter[] | undefined) {
    // console.warn(`UPDATING FILTER PARAMETERS with `, input);
    this._filterParametersActive = input;
  }

  /**
   * Booleans
   */
  isTableChanged(): boolean {
    if (!this.completeColumnList || !this.columnOrderActive) {
      return false;
    }
    if (JSON.stringify(this.columnFilterDefault) !== JSON.stringify(this.columnFilterActive)) {
      // console.log(`ViewModel: IsDirty Test #1`);
      return true;
    }
    if (JSON.stringify(this.columnViewDefault) !== JSON.stringify(this.columnViewActive)) {
      // console.log(`ViewModel: IsDirty Test #2`);
      return true;
    }
    if (JSON.stringify(this.completeColumnList.map((entry) => entry.key)) !== JSON.stringify(this.columnOrderActive)) {
      // console.log(`ViewModel: IsDirty Test #3`);
      return true;
    }
    if (JSON.stringify(this.sortParametersDefault) !== JSON.stringify(this.sortParametersActive)) {
      // console.log(`ViewModel: IsDirty Test #4`);
      return true;
    }
    return false;
  }

  isDifferentFrom(otherModel: ViewModel<T>): boolean {
    if (JSON.stringify(otherModel.columnFilterActive) !== JSON.stringify(this.columnFilterActive)) {
      // console.log(`ViewModel: IsDirty Test #1`);
      return true;
    }
    console.log(`ViewModel: Comparing ColumnViewActive`, this.columnViewActive, otherModel.columnViewActive);
    if (JSON.stringify(otherModel.columnViewActive) !== JSON.stringify(this.columnViewActive)) {
      // console.log(`ViewModel: IsDirty Test #2`);
      return true;
    }
    if (JSON.stringify(otherModel.columnOrderActive) !== JSON.stringify(this.columnOrderActive)) {
      // console.log(`ViewModel: IsDirty Test #3`);
      return true;
    }
    if (JSON.stringify(otherModel.sortParametersActive) !== JSON.stringify(this.sortParametersActive)) {
      // console.log(`ViewModel: IsDirty Test #4`);
      return true;
    }
    return false;
  }

  isDirty(): boolean {
    if (this.isTableChanged()) {
      // console.log(`ViewModel: IsDirty Test #1`);
      return true;
    }
    if (
      JSON.stringify(this.filterParametersDefault?.filter((entry) => entry.dynamic !== true)) !==
      JSON.stringify(this._filterParametersActive?.filter((entry) => entry.dynamic !== true))
    ) {
      /*
        console.log(`ViewModel: IsDirty Test #5 comparing
            ${JSON.stringify(this.filterParametersDefault.filter(entry => entry.dynamic !== true))} to
            ${JSON.stringify(this._filterParametersActive.filter(entry => entry.dynamic !== true))}`);
            */
      return true;
    }
    /* else {
            console.log(`Same filter params:
                ${JSON.stringify(new FilterParameter(this.filterParametersDefault))} -
                ${JSON.stringify(new FilterParameter(this._filterParametersActive))}`);
            return
        }*/
    if (
      this.filterStringDefault !== this.filterStringActive &&
      !(this.filterStringDefault === undefined && this.filterStringActive === '')
    ) {
      // console.log(`ViewModel: IsDirty Test #6`);
      return true;
    }
    return false;
  }

  LookUpDisplayName(key: string): string {
    const response = this.completeColumnList?.find((entry) => entry.key === key);
    return response?.displayName ? response.displayName : 'Unknown';
  }

  /**
   *
   *
   * Conversions
   *
   */
  ToViewConfig(id?: string, name?: string, viewType?: ViewType): ViewConfig<T> {
    //console.log('Converting to Config:', this);
    const response = new ViewConfig<T>();
    response.id = id ?? this.id;
    response.name = name ?? this.name;
    response.type = viewType ? viewType : this.type;
    response.tableName = this.serverObjectType;
    response.dateBar = this.dateBar;
    response.dateBarLabel = this.dateBarLabel;

    response.viewColumns = this.getColumnViewActiveInOrder();
    response.columnOrder = this.columnOrderActive;
    response.filterString = this.filterStringActive;
    // console.log('Getting filter parameters order');
    response.filterParameters = FilterParameter.ToStateOnly(this.filterParametersActive);
    response.filterColumns = this.columnFilterActive;
    response.sortParameters = this.sortParametersActive;
    response.groupBy = this.groupBy;
    response.groupByShiftToColumns = this.groupByShiftToColumns;
    response.groupShiftToColumnsAfter = this.groupShiftToColumnsAfter;
    response.pinnedColumns = this.pinnedColumnsActive;
    response.showNewButton = this.showNewButton;
    response.showFooter = this.showFooter;
    response.groupFooterRows = this.groupFooterRows;
    response.selectable = this.selectable;
    response.sideBar = Array.isArray(this.sideBar) ? [...this.sideBar] : this.sideBar;
    //console.log('Returning Config:', response);
    return response;
  }

  private getEffectiveColumns(allowedCols: number): (keyof T & string)[] {
    const activeColCount = this.columnViewActive?.length ?? 0;
    const lclGroupBy = [...this.groupBy];
    const lclColActive = [...this.columnViewActive];
    if (activeColCount < allowedCols) {
      let groupToShift = allowedCols - activeColCount;
      groupToShift = Math.min(groupToShift, this.groupByShiftToColumns ?? 0);
      for (let i = 0; i < groupToShift; i++) lclColActive.unshift(lclGroupBy.pop() as keyof T & string);
    }
    return lclColActive;
  }

  private getEffectiveGroupBy(allowedCols: number): {
    newGroupBy: (keyof T & string)[];
    groupByToOverFlow: (keyof T & string)[];
  } {
    const activeColCount = this.columnViewActive?.length ?? 1;
    const lclGroupBy = [...this.groupBy];
    const groupByToOverFlow: (keyof T & string)[] = [];
    let groupShifted = 0;
    if (activeColCount < allowedCols) {
      let groupToShift = allowedCols - activeColCount;
      groupToShift = Math.min(groupToShift, this.groupByShiftToColumns ?? 0);
      groupShifted = groupToShift;
      for (let i = 0; i < groupToShift; i++) lclGroupBy.pop();
    }
    for (let i = groupShifted; i < (this.groupByShiftToColumns ?? 0); i++) {
      const groupColName = lclGroupBy.pop() as keyof T & string;
      groupByToOverFlow.unshift(groupColName); //keeping same order
    }
    //console.log(`Effective Group By ${lclGroupBy.join(',')} Overflowed ${groupByToOverFlow.join(',')}`);
    return {
      newGroupBy: lclGroupBy,
      groupByToOverFlow: groupByToOverFlow
    };
  }

  //Returns [ColDef]
  ToAgGridColumnDef(
    appConfig: Partial<AppConfig>,
    maxColumns: number,
    schema: IDisplaySchema<T>,
    cellRenderer: unknown
  ): [ColDef[], (keyof T)[]] {
    const effectiveGroupBy = this.getEffectiveGroupBy(maxColumns);
    const effectiveColumns = this.getEffectiveColumns(maxColumns);
    const lclColAttributeInOrder = this.getCompleteColumnOrderActive();
    //move active Col's to front
    //console.log(`to ag grid: ${this.columnViewActive.join(',')}`);
    const colMove = effectiveColumns.filter((item) => this.columnViewActive.indexOf(item) >= 0);
    for (let i = colMove.length - 1; i >= 0; i--) {
      const groupColName = colMove[i];
      const index = lclColAttributeInOrder.findIndex((entry) => entry?.key == groupColName);
      const groupAttribute = lclColAttributeInOrder[index];
      lclColAttributeInOrder.splice(index, 1);
      lclColAttributeInOrder.unshift(groupAttribute);
    }
    // console.log(`Effective Columns ${effectiveColumns.join(',')}`);
    //Move Group By to front of col list
    const groupColMoved = this.groupBy;
    for (let i = groupColMoved.length - 1; i >= 0; i--) {
      const groupColName = groupColMoved[i];
      const index = lclColAttributeInOrder.findIndex((entry) => entry?.key == groupColName);
      if (index < 0) {
        console.error(`Unable to find ${groupColName.toString()} in column order`);
        continue;
      }
      const groupAttribute = lclColAttributeInOrder[index];
      lclColAttributeInOrder.splice(index, 1);
      lclColAttributeInOrder.unshift(groupAttribute);
    }
    //check for overflow
    const overFlowedCount = effectiveColumns.length - maxColumns;
    const groupOverFlowCount = effectiveGroupBy.groupByToOverFlow.length ?? 0;
    // console.log(
    //   `ViewModel: ToAgGridColumnDef - OverFlowed Count: ${overFlowedCount} Group OverFlow Count: ${groupOverFlowCount}`
    // );
    //console.log(`Effective Group By ${effectiveGroupBy.newGroupBy.join(',')}`);
    const overFlowed = overFlowedCount + groupOverFlowCount > 0;
    let overFlowColumns: (keyof T & string)[] = [];
    if (overFlowed) {
      const baseColumnsMovingToOverflow = overFlowedCount > 0 ? effectiveColumns.splice(-1 * overFlowedCount) : [];
      if (this.groupShiftToColumnsAfter && effectiveColumns.length > this.groupShiftToColumnsAfter) {
        var moveCount = maxColumns - this.groupShiftToColumnsAfter;
        //console.log(`inserting in ${moveCount} columns on the left`);
        overFlowColumns = [...baseColumnsMovingToOverflow];
        for (var i = 0; i < moveCount; i++) {
          const groupAttribute = effectiveGroupBy.groupByToOverFlow.pop();
          //console.log(`Moving ${groupAttribute} to columns`);
          if (!groupAttribute) continue;
          effectiveColumns.unshift(groupAttribute);
          var newOverFlow = effectiveColumns.pop();
          if (!newOverFlow) continue;
          overFlowColumns.unshift(newOverFlow);
          //console.log(`Moved ${groupAttribute} to columns and ${newOverFlow} to overflow`);
        }
        if (effectiveGroupBy.groupByToOverFlow.length > 0) {
          overFlowColumns = [...effectiveGroupBy.groupByToOverFlow, ...overFlowColumns];
        }
      } else {
        overFlowColumns = [...effectiveGroupBy.groupByToOverFlow, ...baseColumnsMovingToOverflow];
      }
    }

    //console.log(`Effective Columns ${effectiveColumns.join(',')} \nOverflowed ${overFlowColumns.join(',')}`);
    //prep sort
    // console.log(
    //   `Sort Parameters:`,
    //   lclColAttributeInOrder.map((m) => m?.propertyName)
    // );
    return [
      lclColAttributeInOrder
        //.filter(col => this.columnViewActive.indexOf(col.propertyName) >= 0)
        .map((attribute, _index) => {
          //console.log(`Attribute: ${attribute.key?.toString()} - ${attribute.displayName}`);
          if (!attribute) return null;
          const groupIndex = effectiveGroupBy?.newGroupBy.indexOf(attribute.key ?? '') ?? -1;
          const sortIndex = this.sortParametersActive?.findIndex((entry) => entry.id === attribute.key) ?? -1;
          const attributeCellRenderer: unknown | string = ViewField.UseCustomRenderer(attribute)
            ? (attribute.renderer ?? cellRenderer)
            : null;
          let lclCellRenderer = attributeCellRenderer;
          // if (attribute.propertyName == 'company')
          //   console.log(`${attribute.propertyName.toString()} attribute:`, lclCellRenderer, groupIndex);
          const lclCellRenderParams = { attribute: attribute } as any;
          if (groupIndex >= 0) {
            lclCellRenderParams['innerRendererParams'] = {
              attribute: attribute
            } as unknown;
          } else if (overFlowed && effectiveColumns.indexOf(attribute.key ?? '') == 0) {
            lclCellRenderer = 'agGroupCellRenderer';
            lclCellRenderParams['innerRenderer'] = attributeCellRenderer ?? cellRenderer;
            lclCellRenderParams['innerRendererParams'] = { attribute: attribute } as unknown;
          }
          //Filter Params
          //Build Filter Params
          let filter: string | undefined = undefined;
          let filterParams = {
            buttons: ['clear']
          } as any; //as Partial<ISetFilterParams>; //af node has issue with this
          switch (attribute.displayType) {
            case DisplayType.DateTimeUtc:
            case DisplayType.DateTimeLocal:
            case DisplayType.DateTimeSecUtc:
            case DisplayType.DateTimeSecLocal:
            case DisplayType.DateLocal:
            case DisplayType.DateUtc:
            case DisplayType.CalendarDateUtc:
              filter = 'agDateColumnFilter';
              break;
            case DisplayType.Enum:
              filter = 'agSetColumnFilter';
              filterParams = {
                keyCreator: (params: any) => params.value,
                valueFormatter: (params: any) =>
                  params.value ? (attribute.displayTypeParameter as SEnumTypeMapping)[params.value] : '(Blanks)',
                comparator: (a: any, b: any) => {
                  const lclA = a.value ? (attribute.displayTypeParameter as SEnumTypeMapping)[a.value] : '(Blanks)';
                  const lclB = b.value ? (attribute.displayTypeParameter as SEnumTypeMapping)[b.value] : '(Blanks)';
                  return lclA >= lclB ? 1 : -1;
                },
                showTooltips: true,
                buttons: ['clear']
              };
              break;
            case DisplayType.Boolean:
              filter = 'agSetColumnFilter';
              filterParams = {
                valueFormatter: (params: any) => (params.value ? 'Yes' : 'No'),
                comparator: (a: any, b: any) => {
                  return a?.toString() >= b?.toString() ? 1 : -1;
                },
                buttons: ['clear']
              };
              break;

            case DisplayType.Name:
              filter = 'agSetColumnFilter';
              filterParams = {
                keyCreator: (params: any) => params.value?.id,
                valueFormatter: (params: any) => params.value?.name ?? '(Blanks)',
                comparator: (a: any, b: any) => {
                  const lclA = a as unknown as { name: string };
                  const lclB = b as unknown as { name: string };
                  console.log(`Name Sorting ${lclA} vs ${lclB}`);
                  return (lclA?.name ?? '(Blanks)') >= (lclB?.name ?? '(Blanks)') ? 1 : -1;
                },
                showTooltips: true,
                buttons: ['clear']
              };
              break;
            case DisplayType.ShortString:
              filter = 'agSetColumnFilter';
              filterParams = {
                keyCreator: (params: any) => params.value?.id,
                valueFormatter: (params: any) =>
                  params.value?.toShortString ? (params.value?.toShortString() ?? '(Blanks)') : '(Blanks)',
                comparator: (a: unknown, b: unknown) => {
                  const lclA = a as unknown as SBaseDisplay;
                  const lclAString = lclA?.toShortString ? (lclA.toShortString() ?? '(Blanks)') : '(Blanks)';
                  const lclB = b as unknown as SBaseDisplay;
                  const lclBString = lclB?.toShortString ? (lclB.toShortString() ?? '(Blanks)') : '(Blanks)';
                  //console.log(`Short String Sorting ${lclA} vs ${lclB}`);
                  return lclAString && lclBString ? (lclAString >= lclBString ? 1 : -1) : 0;
                },
                buttons: ['clear']
              };
              break;
            case DisplayType.ShortStringArray:
              filter = 'agSetColumnFilter';
              filterParams = {
                keyCreator: (params: any) => params.value?.id,
                multiValue: true,
                valueFormatter: (params: any) => {
                  return params.value?.toShortString() ?? '(Blanks)';
                },
                comparator: (a: unknown, b: unknown) => {
                  const lclA = a as unknown as SBaseDisplay;
                  const lclB = b as unknown as SBaseDisplay;
                  return (lclA?.toShortString() ?? '(Blanks)') >= (lclB?.toShortString() ?? '(Blanks)') ? 1 : -1;
                },
                buttons: ['clear']
              };
              break;
            case DisplayType.ShortStringArrayWithParameter:
              filter = 'agSetColumnFilter';
              filterParams = {
                keyCreator: (params: any) => params.value?.id,
                multiValue: true,
                valueFormatter: (params: any) => {
                  return params.value?.toShortString(attribute.displayTypeParameter) ?? '(Blanks)';
                },
                comparator: (a: unknown, b: unknown) => {
                  const lclA = a as unknown as SBaseDisplay;
                  const lclB = b as unknown as SBaseDisplay;
                  return (lclA?.toShortString(attribute.displayTypeParameter as string) ?? '(Blanks)') >=
                    (lclB?.toShortString(attribute.displayTypeParameter as string) ?? '(Blanks)')
                    ? 1
                    : -1;
                },
                buttons: ['clear']
              };
              break;
            case DisplayType.FormattedText:
            case DisplayType.ShortStringArrayFT:
            case DisplayType.ShortStringArrayWithParameterFT:
              filter = 'agSetColumnFilter';
              filterParams = {
                keyCreator: (params) => {
                  return params.value?.lines?.length > 0 ? (params.value?.toString() ?? '(Blanks)') : '(Blanks)';
                  //const lclResponse = params.value?.toFilterValues() ?? [];
                  // if (lclResponse == 0) return '(Blanks)';
                  // console.log('Key', params, lclResponse);
                  // return lclResponse;
                },
                multiValue: true,
                valueFormatter: (params: ValueFormatterParams) => {
                  return params.value?.lines?.length > 0 ? (params.value?.toString() ?? '(Blanks)') : '(Blanks)';
                  // const lclResponse = params.value?.toFilterValues() ?? [];
                  // if (lclResponse == 0) return '(Blanks)';
                  // console.log('FormattedText', params, lclResponse);
                  // return lclResponse;
                },
                comparator: (a: any, b: any) => {
                  return a?.toString() >= b?.toString() ? 1 : -1;
                },
                buttons: ['clear']
              } as Partial<ISetFilterParams>;
              break;
            case undefined:
              filter = 'agSetColumnFilter';
              filterParams = {
                valueFormatter: (params: any) => params.value?.toString(),
                comparator: (a: unknown, b: unknown) => {
                  return (a?.toString() ?? '') >= (b?.toString() ?? '') ? 1 : -1;
                },
                buttons: ['clear']
              };
              break;
            default:
              break;
          }

          //Footer
          const footerAggFunc = SummitAggregateFunctions.GetAggregateFunction<T>(schema, attribute);
          const combinedClassRules = Object.assign(
            {},
            attribute.cellClassRules,
            SummitGridCellFunctions.cellClassRules
          );
          if (ViewField.IsBlurred(attribute, appConfig)) {
            combinedClassRules['blur'] = 'true';
          }
          //console.log('pinned columns', this.pinnedColumnsActive, this.pinnedColumnsDefault);
          //console.log('suppressHeaderMenuButton', this.suppressColumnMenus);
          let cellClass =
            attribute.key != effectiveColumns[effectiveColumns.length - 1]
              ? attribute.displayClass
              : attribute.displayClass + ' pr-3';
          if (lclCellRenderer) {
            cellClass ??= '';
            cellClass += ' h-full';
          }

          var pivotCol = this.pivotColumnActive && this.pivotColumnActive.indexOf(attribute.key ?? '') >= 0;
          //console.log(`Column Column: ${attribute.key} is ${attribute.displayName}`);
          const response = {
            field: attribute.key,
            //Col
            headerName: attribute.displayName,
            headerClass: attribute.headerClass,
            suppressHeaderMenuButton: this.suppressColumnMenus !== false,
            cellClass: cellClass,
            cellClassRules: combinedClassRules,
            //autoHeight: true,
            autoHeight: !attribute.displayType || !DisplayTypeSingleLine.includes(attribute.displayType),
            resizable: true,
            hide:
              effectiveColumns.indexOf(attribute.key ?? '') < 0 ||
              effectiveColumns.indexOf(attribute.key ?? '') >= maxColumns,
            menuTabs: ['filterMenuTab', 'generalMenuTab'],
            pinned: this.pinnedColumnsActive.indexOf(attribute.key ?? '') >= 0 ? 'left' : null,
            //width
            width: attribute.width,
            minWidth: attribute.widthMin,
            maxWidth: attribute.key != effectiveColumns[effectiveColumns.length - 1] ? attribute.widthMax : null,
            flex:
              attribute.key == effectiveColumns[effectiveColumns.length - 1]
                ? 1
                : attribute.widthFlex
                  ? attribute.widthFlex
                  : !(attribute.width || attribute.widthMin || attribute.widthMax)
                    ? 1
                    : null,
            //sortable: true, //Moved to Default
            keyCreator: (params: any) => {
              return params ? schema.getTextLabel(params.data, attribute) : null;
            },
            valueGetter: (params: any) => SummitGridCallbacks.ValueGetter(schema, attribute, params),
            valueFormatter: (params: ValueFormatterParams<T>) => {
              if (params.node?.id == 'bottom' && attribute.displayNullText && !params.value) return null;
              if (attribute.valueFormatterOverride) {
                const override = attribute.valueFormatterOverride(params);
                //console.log(`Override for ${attribute.key?.toString()} is ${override}`);
                if (override !== undefined) return override;
              }
              return params.value !== undefined ? SummitDisplay.formatAutoTextLabel(params.value, attribute) : null;
            },
            //pivot
            pivot: pivotCol,
            //Filtering
            getQuickFilterText: SummitGridCallbacks.getQuickFilterText(
              groupIndex,
              this.columnFilterActive,
              schema,
              attribute
            ),
            filter: filter,
            filterParams: filterParams,
            //Group
            rowGroup: groupIndex >= 0,
            rowGroupIndex: groupIndex >= 0 ? groupIndex : null,
            //sort
            sort: sortIndex >= 0 ? this.sortParametersActive[sortIndex].start : null,
            sortIndex: sortIndex >= 0 ? sortIndex + 1 : null,
            //Footer:
            aggFunc: footerAggFunc,
            /* Expand summary to next column if open*/
            colSpan: (params: any) => {
              if (params.node?.footer || params.node?.id == 'bottom') {
                const colDef = params.column.getColDef();
                if (
                  colDef.aggFunc &&
                  (attribute.footerEquation == EquationTypes.SumBy ||
                    attribute.footerEquation == EquationTypes.SumByAndDisplayBy)
                ) {
                  let colDefs: (ColDef<unknown, unknown> | ColGroupDef<unknown>)[];
                  try {
                    colDefs = !params?.api.isDestroyed() ? (params.api.getColumnDefs() ?? []) : [];
                  } catch {
                    //NOP
                  }
                  colDefs ??= [];
                  //if (!colDefs) return;
                  const colIndex = colDefs?.findIndex((entry) => (entry as ColDef).field == colDef.field);
                  if (colIndex < 0) return 1;
                  colDefs = colDefs.slice(colIndex + 1);
                  const nextColumn = colDefs.find(
                    (entry) => (entry as ColDef).hide != true && (entry as ColDef).rowGroup == false
                  );
                  //console.log(`${colDef.field}: colSpan next column`, nextColumn);
                  return (nextColumn as ColDef)?.aggFunc ? 1 : 2;
                }
              }
              return 1;
            },
            cellRenderer: lclCellRenderer,
            cellRendererParams: lclCellRenderParams,
            //draggable
            rowDrag:
              this.dragEnabled && this.dragEnabled.indexOf(attribute.key ?? '') >= 0
                ? SummitGridCallbacks.isRowDraggable
                : false
            //Export
          } as unknown as ColDef;
          if (attribute.sortParameter) {
            response.comparator = (valueA, valueB, nodeA, nodeB, _isDescending) =>
              SummitGridCallbacks.sortByParameter(attribute.sortParameter as keyof T, nodeA.data, nodeB.data);
          }

          //apply display type config
          switch (attribute.displayType) {
            case DisplayType.Boolean:
            case DisplayType.Enum:
              response.type = ['centerColumn'];
              response.cellClass = this.AddClassIfNeeded(response.cellClass, 'cell-align-center');
              break;
            case DisplayType.Fuel:
            case DisplayType.Money:
            case DisplayType.Financial:
            case DisplayType.Miles:
            case DisplayType.Number:
            case DisplayType.Rate:
            case DisplayType.TimeLength:
            case DisplayType.Percentage:
            case DisplayType.PercentageFromWholeNumber:
              response.type = ['numbersColumn'];
              response.headerClass = this.AddHeaderIfNeeded(response.headerClass, 'header-align-right') as
                | HeaderClass<any, any>
                | undefined;
              response.cellClass = this.AddClassIfNeeded(response.cellClass, 'cell-align-right');
              break;
            case DisplayType.DateLocal:
            case DisplayType.DateTimeLocal:
            case DisplayType.DateTimeSecLocal:
            case DisplayType.DateUtc:
            case DisplayType.DateTimeUtc:
            case DisplayType.DateTimeSecUtc:
              response.type = ['dateColumn'];
              break;
            case DisplayType.FormattedText:
            case DisplayType.ShortStringArray:
            case DisplayType.ShortStringArrayFT:
            case DisplayType.ShortStringArrayWithParameter:
            case DisplayType.ShortStringArrayWithParameterFT:
              response.type = ['multiLine'];
              response.autoHeight = true;
              break;
            default:
              break;
          }
          //console.log(`ColDef ${response.field}`, response);
          return response;
        })
        .filter((col) => col !== null),
      overFlowColumns
    ] as [ColDef[], (keyof T)[]];
  }

  public AddClassIfNeeded(
    currentClass: string | Array<string> | undefined | CellClassFunc,
    newClass: string
  ): string | Array<string> | CellClassFunc {
    if (!currentClass) return newClass;
    const newClassParts = newClass.split('-');
    let classRoot: string | undefined = undefined;
    if (newClassParts.length > 3) {
      classRoot = `${newClassParts[0]}-${newClassParts[1]}`;
      //console.log(`Class Root: ${classRoot}`);
    }
    if (isString(currentClass)) {
      if (classRoot && currentClass.indexOf(classRoot) >= 0) {
        //console.log(`Found ${classRoot} in ${currentClass}, skipping adding ${newClass}`);
        return currentClass;
      }
      return currentClass + ' ' + newClass;
    }
    if (Array.isArray(currentClass)) {
      if (isString(classRoot) && currentClass.findIndex((entry) => entry.startsWith(classRoot as string)) >= 0) {
        //console.log(`Found ${classRoot} in ${currentClass}, skipping adding ${newClass}`);
        return currentClass;
      }
      currentClass.push(newClass);
      return currentClass;
    }
    return currentClass;
  }

  public AddHeaderIfNeeded(currentClass: HeaderClass<unknown, unknown> | undefined, newClass: string): HeaderClass {
    if (!currentClass) return newClass;
    const newClassParts = newClass.split('-');
    let classRoot: string | undefined = undefined;
    if (newClassParts.length > 3) {
      classRoot = `${newClassParts[0]}-${newClassParts[1]}`;
      //console.log(`Class Root: ${classRoot}`);
    }
    if (isString(currentClass)) {
      if (classRoot && currentClass.indexOf(classRoot) >= 0) {
        //console.log(`Found ${classRoot} in ${currentClass}, skipping adding ${newClass}`);
        return currentClass;
      }
      return currentClass + ' ' + newClass;
    }
    if (Array.isArray(currentClass)) {
      if (isString(classRoot) && currentClass.findIndex((entry) => entry.startsWith(classRoot as string)) >= 0) {
        //console.log(`Found ${classRoot} in ${currentClass}, skipping adding ${newClass}`);
        return currentClass;
      }
      currentClass.push(newClass);
      return currentClass;
    }
    return currentClass;
  }

  public SetFilter(filterName: string, state: boolean, data?: unknown) {
    //console.log(`Setting Filter ${filterName} to ${state}`, this.refFilterParameters);
    const lclFilter = this.filterParametersActive.find((entry) => entry.id === filterName);
    if (!lclFilter) {
      /*
      rg4js('send', {
        error: `Could not find filter ${filterName} in Schema for ${this.refTitle} for object ${this.refServerObjectName}`
      });
      */
      return;
    }
    lclFilter.state = state;
    if (data != null && data != undefined) {
      if (SDate.isDayjs(data)) {
        lclFilter.filterData = data.format('YYYY-MM-DD');
      } else {
        lclFilter.filterData = data;
      }
    }
  }

  public SafeGuardFilter(activeProfile: IProfile) {
    //Fill in any missing filter parameters
    //console.log(`processing`);
    if (this.filterParametersActive) {
      this.filterParametersActive.forEach((entry) => {
        //console.log(`processing ${entry.id}`);
        if (entry.state === true && entry.oDataFilter) {
          //console.log(`processing ${entry.id}- stage 2: ${entry.filterData?.toString()}`, entry);
          if (
            entry.oDataFilter.indexOf(`[${entry.id}]`) >= 0 &&
            (!isString(entry.filterData) || entry.filterData === null || entry.filterData === undefined)
          ) {
            //console.log(`processing ${entry.id}- stage 3`);
            switch (entry.id) {
              case 'employeeID':
                entry.filterData = activeProfile.id;
                break;
              case 'fedExID':
                entry.filterData = activeProfile.fedExID;
                break;
              default:
                break;
            }
          }
        }
      });
    }
  }
}
